import {
  Badge,
  Button,
  Card,
  Checkbox,
  DateTimePicker,
  InputDropdown,
  Modal,
  ModalBody,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Tooltip,
} from "@onengine/component-library";
import { Box, Flex, Stack, Text, Title } from "@mantine/core";
import {
  IconCircleCheck,
  IconInfoSquareRounded,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { createFileRoute, retainSearchParams, useNavigate } from "@tanstack/react-router";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import styles from "./accept-work-order.module.scss";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Radio } from "@/components/ui/Radio/Radio";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/Input/Input";
import {
  acceptWorkOrderFormSchema,
  companionModalSchema,
  NEW_COMPANION_OPTION_VALUE,
} from "@/utils/acceptWorkOrder/acceptWorkOrderSchema";
import SelectCompanionModal from "@/components/features/WorkOrders/AcceptWorkOrder/SelectCompanionModal/SelectCompanionModal";
import { useCompanionRelation } from "@/hooks/work-order/acceptWorkOrder/useCompnaionRelationQueries";
import { useAcceptWorkOrder } from "@/hooks/work-orders/acceptWorkOrder/useAcceptWorkOrderMutations";
import { useGetWorkOrderHeaderData } from "@/hooks/work-orders/details/useOverviewQueries";
import {
  useGetCompanionFrequencyOptions,
  useGetCompanionLaborOptions,
  useGetCompanionList,
} from "@/hooks/work-orders/acceptWorkOrder/useAcceptWorkOrderQueries";
import type {
  ICompanion,
  ICompanionLookupOption,
} from "@/api/work-orders/acceptWorkOrder/acceptWorkOrder.types";
import type { Trade } from "@/api/work-orders/details/wo-details.queries.types";
import type { AcceptWorkOrderAssignedResponse } from "@/api/work-orders/acceptWorkOrder/acceptWorkOrder.mutations.types";
import { handleApiSuccess, showErrorAlert } from "@/utils/responseHandler";
import { enforcePhoneNumberFormatInInput } from "@/utils/formatPhoneNumber";
import { getVisitTypeBadgeColors } from "@/utils/badgeConfig";
import { formatIsoDateToDisplayDateTime } from "@/utils/dateFormatter";
import { formatCurrency } from "@/utils/formatCurrency";
import { SpOverviewSection } from "@/components/features/WorkOrders/VisitDetail/SpOverviewSection/SpOverviewSection";
import TableActionsMenu from "@/components/ui/TableActionsMenu/TableActionsMenu";

declare module "@tanstack/react-router" {
  interface HistoryState {
    acceptResponse?: AcceptWorkOrderAssignedResponse;
  }
}

export const Route = createFileRoute("/work-orders/$workOrderId/accept-work-order/")({
  component: AcceptWorkOrder,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
});

function AcceptWorkOrder() {
  const navigate = useNavigate();
  const { workOrderId } = Route.useParams();
  const [editingCompanionIndex, setEditingCompanionIndex] = useState<number | null>(null);
  const [companionRelation, setCompanionRelation] = useState("");
  const [companionIsHelper18Plus, setCompanionIsHelper18Plus] = useState(false);
  const [isCompanionModalOpen, setIsCompanionModalOpen] = useState(false);
  const [isSelectCompanionModalOpen, setIsSelectCompanionModalOpen] = useState(false);
  const [selectedExistingCompanions, setSelectedExistingCompanions] = useState<string[]>([]);
  const [companionModalIndex, setCompanionModalIndex] = useState<number | null>(null);
  const [ackHelper18Plus, setAckHelper18Plus] = useState(false);
  const [ackHelpersOnly, setAckHelpersOnly] = useState(false);
  const [ackAccountability, setAckAccountability] = useState(false);
  const [companionSelectOption, setCompanionSelectOption] = useState("");
  const [companionFullName, setCompanionFullName] = useState("");
  const [companionFrequency, setCompanionFrequency] = useState("");
  const [companionLabor, setCompanionLabor] = useState("");
  const [companionCanOnboard, setCompanionCanOnboard] = useState("");
  const [companionPhoneNumber, setCompanionPhoneNumber] = useState("");
  const [modalErrors, setModalErrors] = useState<Record<string, string>>({});

  const { data: companionFrequencyData } = useGetCompanionFrequencyOptions();
  const { data: companionLaborData } = useGetCompanionLaborOptions();

  const companionFrequencyList =
    (companionFrequencyData as unknown as ICompanionLookupOption[]) ?? [];
  const companionLaborList = (companionLaborData as unknown as ICompanionLookupOption[]) ?? [];

  const companionFrequencyOptions = companionFrequencyList.map((item) => ({
    value: String(item.id),
    label: item.label,
  }));

  const companionLaborOptions = companionLaborList.map((item) => ({
    value: String(item.id),
    label: item.label,
  }));

  const yesNoOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const companionOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];
  const { data: workOrderHeaderData } = useGetWorkOrderHeaderData(workOrderId);
  const { data: companionData } = useCompanionRelation();
  const { data: companionListData, isPending: isCompanionListLoading } = useGetCompanionList();

  const companionList = (companionListData as unknown as ICompanion[]) ?? [];

  const companionCheckboxOptions = companionList.map((c) => ({
    value: c.id,
    label: (
      <Stack gap={2}>
        <Box style={{ fontWeight: 500 }}>{c.relationFullName}</Box>
        <Box style={{ fontSize: 12, opacity: 0.6 }}>{c.relation.label}</Box>
      </Stack>
    ),
  }));

  const { mutate: acceptWorkOrderMutation, isPending: isAcceptingWorkOrder } = useAcceptWorkOrder();

  const companionValueOptions =
    companionData
      ?.filter((item: any) => item?.id != null && item?.label != null)
      .map((item: any) => ({
        value: String(item.id),
        label: String(item.label),
      })) ?? [];

  const getRelationLabel = (relation: string) =>
    companionValueOptions.find((opt: { value: string; label: string }) => opt.value === relation)
      ?.label ?? relation;

  const isServiceAnimal =
    !!companionRelation &&
    getRelationLabel(companionRelation).trim().toLowerCase() === "service animal";

  const isHelper =
    !!companionRelation && getRelationLabel(companionRelation).trim().toLowerCase() === "helper";

  const methods = useForm({
    resolver: zodResolver(acceptWorkOrderFormSchema),
    defaultValues: {
      acceptWorkOrderDateTime: null,
      hasCompanions: undefined,
      companions: [],
    },
    mode: "onSubmit",
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = methods;

  const companions = watch("hasCompanions");
  const companionsList = watch("companions") || [];
  const acceptWorkOrderDateTime = watch("acceptWorkOrderDateTime") as Date | null;

  useEffect(() => {
    if (companions === "no") {
      setValue("companions", []);
      setAckHelper18Plus(false);
      setAckHelpersOnly(false);
      setAckAccountability(false);
    }
  }, [companions, setValue]);

  const allAcknowledgmentsChecked = ackHelper18Plus && ackHelpersOnly && ackAccountability;

  useEffect(() => {
    const scheduledTime = workOrderHeaderData?.data.nextVisit.scheduledTime;
    if (!scheduledTime || !acceptWorkOrderDateTime) return;

    const maxDate = new Date(scheduledTime);
    const maxMins = maxDate.getHours() * 60 + maxDate.getMinutes();
    const selectedMins =
      acceptWorkOrderDateTime.getHours() * 60 + acceptWorkOrderDateTime.getMinutes();

    if (selectedMins > maxMins) {
      const maxH = maxDate.getHours();
      const maxM = maxDate.getMinutes();
      const maxAp = maxH >= 12 ? "PM" : "AM";
      const maxH12 = maxH % 12 === 0 ? 12 : maxH % 12;
      setError("acceptWorkOrderDateTime", {
        message: `Arrival time cannot exceed ${maxH12}:${String(maxM).padStart(2, "0")} ${maxAp}`,
      });
    } else {
      clearErrors("acceptWorkOrderDateTime");
    }
  }, [
    acceptWorkOrderDateTime,
    workOrderHeaderData?.data.nextVisit.scheduledTime,
    setError,
    clearErrors,
  ]);

  const resetCompanionModalFields = () => {
    setCompanionSelectOption("");
    setCompanionRelation("");
    setCompanionFullName("");
    setCompanionIsHelper18Plus(false);
    setCompanionFrequency("");
    setCompanionLabor("");
    setCompanionCanOnboard("");
    setCompanionPhoneNumber("");
    setModalErrors({});
  };

  const clearModalError = (field: string) => {
    setModalErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleAddCompanion = () => {
    setEditingCompanionIndex(null);
    setCompanionModalIndex(companionsList.length);
    resetCompanionModalFields();
    setIsCompanionModalOpen(true);
  };

  const handleEditCompanion = (index: number) => {
    const companion = companionsList[index];
    setEditingCompanionIndex(index);
    setCompanionModalIndex(index);
    setCompanionSelectOption(companion.existingId ?? NEW_COMPANION_OPTION_VALUE);
    setCompanionRelation(companion.relation);
    setCompanionFullName(
      [companion.firstName, companion.lastName].filter(Boolean).join(" ").trim(),
    );
    setCompanionIsHelper18Plus(companion.isHelper18Plus);
    setCompanionFrequency(companion.frequency ?? "");
    setCompanionLabor(companion.labor ?? "");
    setCompanionCanOnboard(companion.canOnboard ?? "");
    setCompanionPhoneNumber(companion.phoneNumber ?? "");
    setModalErrors({});
    setIsCompanionModalOpen(true);
  };

  const handleDeleteCompanion = (index: number) => {
    const updatedCompanions = companionsList.filter((_, i) => i !== index);
    setValue("companions", updatedCompanions, { shouldValidate: true });
  };

  const validateAndBuildCompanion = () => {
    const parsed = companionModalSchema.safeParse({
      selectedOption: companionSelectOption,
      relation: companionRelation,
      fullName: companionFullName,
      canAssist: isHelper,
      frequency: companionFrequency,
      labor: companionLabor,
      canOnboard: companionCanOnboard,
      phoneNumber: companionPhoneNumber,
      isServiceAnimal,
      isHelper,
    });

    if (!parsed.success) {
      const errs: Record<string, string> = {};
      parsed.error.issues.forEach((issue) => {
        const key = String(issue.path[0]);
        if (!errs[key]) errs[key] = issue.message;
      });
      setModalErrors(errs);
      return null;
    }

    setModalErrors({});

    const isExisting =
      !!companionSelectOption && companionSelectOption !== NEW_COMPANION_OPTION_VALUE;

    if (isServiceAnimal) {
      return {
        ...(isExisting && { existingId: companionSelectOption }),
        relation: companionRelation,
        firstName: "",
        lastName: "",
        canAssist: false,
        isHelper18Plus: false,
      };
    }

    return {
      ...(isExisting && { existingId: companionSelectOption }),
      relation: companionRelation,
      firstName: companionFullName.trim(),
      lastName: "",
      canAssist: isHelper,
      isHelper18Plus: companionIsHelper18Plus,
      ...(isHelper && {
        frequency: companionFrequency,
        labor: companionLabor,
        canOnboard: companionCanOnboard as "yes" | "no",
        ...(companionCanOnboard === "yes" && {
          phoneNumber: companionPhoneNumber.trim(),
        }),
      }),
    };
  };

  const handleSaveCompanion = () => {
    const newCompanion = validateAndBuildCompanion();
    if (!newCompanion) return;

    if (editingCompanionIndex !== null) {
      const updatedCompanions = [...companionsList];
      updatedCompanions[editingCompanionIndex] = newCompanion;
      setValue("companions", updatedCompanions, { shouldValidate: true });
    } else {
      setValue("companions", [...companionsList, newCompanion], { shouldValidate: true });
    }

    setIsCompanionModalOpen(false);
    resetCompanionModalFields();
    setEditingCompanionIndex(null);
    setCompanionModalIndex(null);
  };

  const handleSaveAndAddAnotherCompanion = () => {
    const newCompanion = validateAndBuildCompanion();
    if (!newCompanion) return;

    const nextList =
      editingCompanionIndex !== null
        ? companionsList.map((c, i) => (i === editingCompanionIndex ? newCompanion : c))
        : [...companionsList, newCompanion];
    setValue("companions", nextList, { shouldValidate: true });

    resetCompanionModalFields();
    setEditingCompanionIndex(null);
    setCompanionModalIndex(nextList.length);
  };

  const handleCancelCompanion = () => {
    setIsCompanionModalOpen(false);
    resetCompanionModalFields();
    setEditingCompanionIndex(null);
    setCompanionModalIndex(null);
  };

  const handleClose = () => {
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
    });
  };

  const handleAddSelectedCompanions = () => {
    if (selectedExistingCompanions.length === 0) return;

    const selected = companionList
      .filter((c) => selectedExistingCompanions.includes(c.id))
      .map((c) => ({
        existingId: c.id,
        firstName: c.relationFullName,
        relation: c.relation.label,
        canAssist: c.canAssist,
        isHelper18Plus: c.isHelper18Plus,
      }));

    // Prevent duplicates — existing companions deduped by id, new by name
    const merged = [...companionsList, ...selected];
    const unique = merged.filter((item, index, self) => {
      if (item.existingId) {
        return index === self.findIndex((t) => t.existingId === item.existingId);
      }
      const a = item as { firstName?: string; lastName?: string };
      return (
        index ===
        self.findIndex((t) => {
          const b = t as { firstName?: string; lastName?: string };
          return b.firstName === a.firstName && b.lastName === a.lastName;
        })
      );
    });

    setValue("companions", unique, { shouldValidate: true });

    setIsSelectCompanionModalOpen(false);
    setSelectedExistingCompanions([]);
  };

  const handleAccept = handleSubmit((data) => {
    const scheduledTime = workOrderHeaderData?.data.nextVisit.scheduledTime;
    if (scheduledTime && data.acceptWorkOrderDateTime) {
      const maxDate = new Date(scheduledTime);
      const maxMins = maxDate.getHours() * 60 + maxDate.getMinutes();
      const selectedMins =
        data.acceptWorkOrderDateTime.getHours() * 60 + data.acceptWorkOrderDateTime.getMinutes();
      if (selectedMins > maxMins) {
        const maxH = maxDate.getHours();
        const maxM = maxDate.getMinutes();
        const maxAp = maxH >= 12 ? "PM" : "AM";
        const maxH12 = maxH % 12 === 0 ? 12 : maxH % 12;
        setError("acceptWorkOrderDateTime", {
          message: `Arrival time cannot exceed ${maxH12}:${String(maxM).padStart(2, "0")} ${maxAp}`,
        });
        return;
      }
    }

    const formCompanions = data.companions ?? [];
    const companions = formCompanions.map((c) => {
      const isCompanionServiceAnimal =
        !!c.relation && getRelationLabel(c.relation).trim().toLowerCase() === "service animal";
      const relationFullName = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim();
      const entry: Record<string, unknown> = {
        id: c.existingId ?? null,
        relationId: Number(c.relation),
        isHelper18Plus: c.isHelper18Plus,
        canAssist: c.canAssist,
      };
      if (isCompanionServiceAnimal) {
        // SERV-2872: an existing companion switched to Service Animal has to have
        // its stored name cleared. Omitting the key means leave it alone, which
        // is what left the previous person's name on the row. A companion being
        // added has no stored name to clear, and the add schema does not accept
        // null, so only an existing row says it.
        if (c.existingId) {
          entry.relationFullName = null;
        }
      } else if (relationFullName) {
        entry.relationFullName = relationFullName;
      }
      if (c.frequency) entry.howOftenAccompaniesId = Number(c.frequency);
      if (c.labor) entry.howMuchAssistanceId = Number(c.labor);
      if (c.canOnboard) entry.canOnboard = c.canOnboard === "yes";
      if (c.phoneNumber) entry.phoneNumber = c.phoneNumber;
      return entry;
    });

    const arrivalDateTime = data.acceptWorkOrderDateTime as Date;
    const hasCompanions = data.hasCompanions === "yes";

    const payload: Record<string, unknown> = {
      visitId: workOrderHeaderData?.data.nextVisit.id,
      status: "accept",
      arrivalDateTime: arrivalDateTime.toISOString(),
      hasCompanions,
      ...(hasCompanions && {
        acknowledgments: {
          helpers18Plus: true,
          onlyHelpersEnterSites: true,
          spAccountableForHelpers: true,
        },
      }),
      // SERV-2872: keyed off the flag this builder already holds rather than
      // relying on an effect elsewhere in the file to have emptied the list.
      ...(hasCompanions && companions.length > 0 && { companions }),
    };

    acceptWorkOrderMutation(
      { workOrderId, payload },
      {
        onSuccess: (response) => {
          const result = response.data;
          if (result.outcome === "already_assigned") {
            showErrorAlert(
              result.message ??
                "This work order has already been claimed by another service provider.",
              "Work Order Unavailable",
            );
            navigate({
              to: "/work-orders/$workOrderId/accept-work-order/work-order-filled",
              params: { workOrderId },
            });
            return;
          }
          if (result.outcome === "assigned") {
            handleApiSuccess("Successfully accepted the work order.", "Accepted Work Order");
            navigate({
              to: "/work-orders/$workOrderId/accept-work-order/work-order-accepted",
              params: { workOrderId },
              state: { acceptResponse: result },
            });
          }
        },
      },
    );
  });

  const workOrderDetails = (
    workOrderHeaderData?.data as unknown as {
      workOrderDetails?: {
        workOrderId?: string;
        nteAmount?: string | number;
        expectedOnsiteDate?: string;
      };
    }
  )?.workOrderDetails;

  const formatTrades = (trades: Trade[]) => {
    if (trades.length === 0) return "";
    const [first, ...rest] = trades;
    if (rest.length === 0) return first.tradeName;
    return `${first.tradeName}, Trade +${rest.length}`;
  };

  const workerOrderHeaderInfo: { label: string; value: React.ReactNode }[] = [
    {
      label: "Work Order",
      value: workOrderDetails?.workOrderId ?? "",
    },
    {
      label: "Trades",
      value: <div>{formatTrades(workOrderHeaderData?.data.trades ?? [])}</div>,
    },
    {
      label: "Initial NTE",
      value: (
        <div>
          {workOrderDetails?.nteAmount != null
            ? formatCurrency(Number(workOrderDetails.nteAmount), {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : ""}
        </div>
      ),
    },
    {
      label: "Visit Type",
      value: (
        <div>
          <Badge
            size="medium"
            {...getVisitTypeBadgeColors(workOrderHeaderData?.data.nextVisit.visitType.label ?? "")}
          >
            {workOrderHeaderData?.data.nextVisit.visitType.label ?? ""}
          </Badge>
        </div>
      ),
    },
    {
      label: "Expected Onsite Date",
      value: (
        <div>
          {formatIsoDateToDisplayDateTime(workOrderHeaderData?.data?.expectedOnsiteDate ?? "")}
        </div>
      ),
    },
  ];

  return (
    <Box>
      <section className={styles.section}>
        <Flex justify={"space-between"} align={"center"} className={styles.header}>
          <Title order={3} className={styles.title}>
            Accept a Work Order
          </Title>
          <IconX size={16} className={styles.closeIcon} onClick={handleClose} aria-label="Close" />
        </Flex>
        <Box className={styles.container}>
          <Card padding={"1px"}>
            <Flex style={{ alignItems: "center", width: "100%" }}>
              <SpOverviewSection
                overViewData={workerOrderHeaderInfo}
                customStyles={{ justifyContent: "space-between" }}
              />
            </Flex>
          </Card>
          <Card padding={"1px"}>
            <Flex gap="xl" style={{ width: "100%" }}>
              <FormProvider {...methods}>
                <form className={styles.formWrapper} style={{ width: "100%" }}>
                  <Stack gap={"var(--spacing-2x-large, 32px)"} style={{ flex: 1 }}>
                    <Stack gap={"var(--spacing-x-large, 24px)"}>
                      <Text className={styles.headerText}>Arrival Details</Text>
                      <Text className={styles.headerDesc}>
                        Please provide the following details to accept this work order:
                      </Text>
                    </Stack>
                    <Stack gap={"var(--spacing-x-large, 24px)"}>
                      <Controller
                        name="acceptWorkOrderDateTime"
                        control={control}
                        render={({ field: { value, onChange } }) => {
                          const dateValue = (value as Date | null) ?? null;
                          const timeValue = dateValue
                            ? (() => {
                                const h24 = dateValue.getHours();
                                const m = dateValue.getMinutes();
                                const ap = h24 >= 12 ? "PM" : "AM";
                                const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
                                return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ap}`;
                              })()
                            : "";
                          return (
                            <DateTimePicker
                              label="Arrival Date & Time"
                              dateValue={dateValue}
                              timeValue={timeValue || undefined}
                              datePlaceholder="MM / DD / YYYY"
                              timePlaceholder="--:-- --"
                              error={errors.acceptWorkOrderDateTime?.message as string}
                              onChange={(dateTime) => onChange(dateTime)}
                              width={"300px"}
                            />
                          );
                        }}
                      />
                    </Stack>
                    <Stack gap={"var(--spacing-x-large, 24px)"}>
                      <Radio
                        name="hasCompanions"
                        label="Will you have any companions?"
                        options={companionOptions}
                        direction="row"
                        error={errors.hasCompanions?.message}
                      />

                      {companions === "yes" && (
                        <Stack gap="var(--spacing-medium)" style={{ width: "100%" }}>
                          {!allAcknowledgmentsChecked && (
                            <Stack gap="var(--spacing-small)">
                              <Text className={styles.headerDesc}>
                                Please acknowledge the following before adding companions:
                              </Text>
                              <Checkbox
                                label="Helpers must be 18+ and appropriately dressed for the site."
                                state={ackHelper18Plus ? "selected" : "deselected"}
                                onValueChange={(checked) => setAckHelper18Plus(checked)}
                                styles={{
                                  body: {
                                    gap: "var(--spacing-x-small, 8px)",
                                  },
                                }}
                              />
                              <Checkbox
                                label="Only helpers may enter client sites; other companions must be of legal age and stay outside or in the vehicle."
                                state={ackHelpersOnly ? "selected" : "deselected"}
                                onValueChange={(checked) => setAckHelpersOnly(checked)}
                                styles={{
                                  body: {
                                    gap: "var(--spacing-x-small, 8px)",
                                  },
                                }}
                              />
                              <Checkbox
                                label="Helpers are part of the SP's team; the SP is accountable for their actions and assumes all related risks."
                                state={ackAccountability ? "selected" : "deselected"}
                                onValueChange={(checked) => setAckAccountability(checked)}
                                styles={{
                                  body: {
                                    gap: "var(--spacing-x-small, 8px)",
                                  },
                                }}
                              />
                            </Stack>
                          )}

                          {allAcknowledgmentsChecked && (
                            <Stack gap="var(--spacing-medium, 16px)" style={{ width: "100%" }}>
                              <Flex gap={4} align="center">
                                <Badge
                                  color="success"
                                  variant="lowContrast"
                                  size="medium"
                                  iconLeft={
                                    <IconCircleCheck
                                      size={16}
                                      color="var(--text-success-strong, #054A2E)"
                                    />
                                  }
                                >
                                  <Text className={styles.badgeText}>
                                    Companion agreements complete
                                  </Text>
                                </Badge>
                                <Tooltip title="Anyone assisting with the work order must be 18 or older and dressed appropriately for the site. ">
                                  <IconInfoSquareRounded size={13} />
                                </Tooltip>
                              </Flex>

                              {errors.companions?.message && (
                                <Text c="red" size="sm">
                                  {errors.companions.message as string}
                                </Text>
                              )}

                              {companionsList.map((companion, index) => {
                                const companionFullName = [companion.firstName, companion.lastName]
                                  .filter(Boolean)
                                  .join(" ");
                                const companionRelationLabel = companion.relation
                                  ? getRelationLabel(companion.relation)
                                  : "";
                                const subtitleName = companionFullName || `Companion ${index + 1}`;
                                const subtitleSuffix = companionRelationLabel
                                  ? `${companionRelationLabel}`
                                  : "";
                                return (
                                  <Box key={index} className={styles.companionCard}>
                                    <Flex
                                      justify="space-between"
                                      align="center"
                                      style={{ width: "100%" }}
                                    >
                                      <Box
                                        style={{ flex: 1, cursor: "pointer" }}
                                        onClick={() => handleEditCompanion(index)}
                                      >
                                        <Text className={styles.companionCardTitle}>
                                          {subtitleName}
                                        </Text>
                                        <Text className={styles.companionCardSubtitle}>
                                          {subtitleSuffix}
                                        </Text>
                                      </Box>
                                      <Button
                                        onClick={() => handleDeleteCompanion(index)}
                                        className={styles.deleteButton}
                                        variant="tertiary"
                                      >
                                        <Flex align={"center"} gap={"var(--spacing-3x-small, 4px)"}>
                                          <IconTrash size={16} />
                                          <Text className={styles.deleteButtonText}>Delete</Text>
                                        </Flex>
                                      </Button>
                                      <TableActionsMenu
                                        options={[
                                          {
                                            btnText: "Edit",
                                            onClickHandler: () => handleEditCompanion(index),
                                          },
                                        ]}
                                      />
                                    </Flex>
                                  </Box>
                                );
                              })}

                              <Button
                                variant="secondary"
                                onClick={handleAddCompanion}
                                className={styles.addCompanionButton}
                              >
                                <Flex align={"center"} gap={"var(--spacing-3x-small, 4px)"}>
                                  <IconPlus size={16} />
                                  <Text className={styles.addCompanionButtonText}>
                                    New Companion
                                  </Text>
                                </Flex>
                              </Button>
                            </Stack>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </form>
              </FormProvider>
            </Flex>
          </Card>

          {/* Companion Modal */}
          <Modal open={isCompanionModalOpen} onClose={handleCancelCompanion} size={760}>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>
                  {`Companion ${companionModalIndex !== null ? companionModalIndex + 1 : ""}`}
                </ModalTitle>
                <ModalClose onClick={handleCancelCompanion} />
              </ModalHeader>
              <ModalBody>
                <Stack gap="var(--spacing-large)">
                  <InputDropdown
                    label="Who will accompany you?"
                    placeholder="Select"
                    items={[
                      ...companionList
                        .filter((c) => c?.id != null)
                        .map((c) => ({
                          value: String(c.id),
                          label: c.relationFullName ?? `${c.relation.label}`,
                        })),
                      { value: NEW_COMPANION_OPTION_VALUE, label: "New Companion" },
                    ]}
                    value={companionSelectOption || null}
                    onChange={(value) => {
                      const next = value ?? "";
                      setCompanionSelectOption(next);
                      const existing =
                        next && next !== NEW_COMPANION_OPTION_VALUE
                          ? companionList.find((c) => c.id === next)
                          : undefined;
                      if (existing) {
                        setCompanionRelation(String(existing.relationId));
                        setCompanionFullName(existing.relationFullName ?? "");
                        setCompanionIsHelper18Plus(existing.isHelper18Plus);
                        setCompanionFrequency(
                          existing.howOftenAccompaniesId != null
                            ? String(existing.howOftenAccompaniesId)
                            : "",
                        );
                        setCompanionLabor(
                          existing.howMuchAssistanceId != null
                            ? String(existing.howMuchAssistanceId)
                            : "",
                        );
                        setCompanionCanOnboard(existing.canOnboard ? "yes" : "no");
                        setCompanionPhoneNumber(
                          enforcePhoneNumberFormatInInput(existing.Phones?.[0]?.phoneNumber ?? ""),
                        );
                        setModalErrors({});
                      } else {
                        setCompanionRelation("");
                        setCompanionFullName("");
                        setCompanionIsHelper18Plus(false);
                        setCompanionFrequency("");
                        setCompanionLabor("");
                        setCompanionCanOnboard("");
                        setCompanionPhoneNumber("");
                      }
                      clearModalError("selectedOption");
                    }}
                    size="large"
                    style={{ maxWidth: 347 }}
                    error={modalErrors.selectedOption}
                  />

                  {companionSelectOption !== "" && (
                    <Stack gap="var(--spacing-medium)">
                      <Flex gap="var(--spacing-medium)">
                        <Box style={{ flex: 1 }}>
                          <InputDropdown
                            label="What is the companions relation to you?"
                            placeholder="Select"
                            items={companionValueOptions}
                            value={companionRelation || null}
                            onChange={(value) => {
                              const nextRelation = value ?? "";
                              setCompanionRelation(nextRelation);
                              clearModalError("relation");
                              const nextLabel = nextRelation
                                ? getRelationLabel(nextRelation).trim().toLowerCase()
                                : "";
                              const nextIsServiceAnimal = nextLabel === "service animal";
                              const nextIsHelper = nextLabel === "helper";
                              if (nextIsServiceAnimal) {
                                setCompanionFullName("");
                                setCompanionFrequency("");
                                setCompanionLabor("");
                                setCompanionCanOnboard("");
                                setCompanionPhoneNumber("");
                                setModalErrors((prev) => {
                                  const {
                                    fullName: _fn,
                                    frequency: _f,
                                    labor: _l,
                                    canOnboard: _c,
                                    phoneNumber: _p,
                                    ...rest
                                  } = prev;
                                  return rest;
                                });
                              } else if (!nextIsHelper) {
                                setCompanionFrequency("");
                                setCompanionLabor("");
                                setCompanionCanOnboard("");
                                setCompanionPhoneNumber("");
                                setModalErrors((prev) => {
                                  const {
                                    frequency: _f,
                                    labor: _l,
                                    canOnboard: _c,
                                    phoneNumber: _p,
                                    ...rest
                                  } = prev;
                                  return rest;
                                });
                              }
                            }}
                            size="large"
                            error={modalErrors.relation}
                          />
                        </Box>
                        <Box style={{ flex: 1 }}>
                          {!isServiceAnimal && (
                            <Input
                              label="Full Name of the Companion"
                              placeholder="Enter"
                              value={companionFullName}
                              maxLength={100}
                              onChange={(value) => {
                                setCompanionFullName(value);
                                clearModalError("fullName");
                              }}
                              size="large"
                              error={modalErrors.fullName}
                            />
                          )}
                        </Box>
                      </Flex>

                      {isHelper && (
                        <>
                          <Flex gap="var(--spacing-medium)">
                            <Box style={{ flex: 1 }}>
                              <InputDropdown
                                label="How often do they accompany you?"
                                placeholder="Select"
                                items={companionFrequencyOptions}
                                value={companionFrequency || null}
                                onChange={(value) => {
                                  setCompanionFrequency(value ?? "");
                                  clearModalError("frequency");
                                }}
                                size="large"
                                error={modalErrors.frequency}
                              />
                            </Box>
                            <Box style={{ flex: 1 }}>
                              <InputDropdown
                                label="What labor do they assist with?"
                                placeholder="Select"
                                items={companionLaborOptions}
                                value={companionLabor || null}
                                onChange={(value) => {
                                  setCompanionLabor(value ?? "");
                                  clearModalError("labor");
                                }}
                                size="large"
                                error={modalErrors.labor}
                              />
                            </Box>
                          </Flex>

                          <Flex gap="var(--spacing-medium)">
                            <Box style={{ flex: 1 }}>
                              <InputDropdown
                                label="Can we onboard them?"
                                placeholder="Select"
                                items={yesNoOptions}
                                value={companionCanOnboard || null}
                                onChange={(value) => {
                                  const next = value ?? "";
                                  setCompanionCanOnboard(next);
                                  if (next !== "yes") setCompanionPhoneNumber("");
                                  clearModalError("canOnboard");
                                }}
                                size="large"
                                error={modalErrors.canOnboard}
                              />
                            </Box>
                            <Box style={{ flex: 1 }}>
                              {companionCanOnboard === "yes" && (
                                <Input
                                  label="Phone Number"
                                  placeholder="Enter"
                                  value={companionPhoneNumber}
                                  maxLength={12}
                                  onChange={(value) => {
                                    setCompanionPhoneNumber(enforcePhoneNumberFormatInInput(value));
                                    clearModalError("phoneNumber");
                                  }}
                                  size="large"
                                  error={modalErrors.phoneNumber}
                                />
                              )}
                            </Box>
                          </Flex>
                        </>
                      )}
                    </Stack>
                  )}

                  <Flex justify="space-between" align="center">
                    <Button
                      variant="tertiary"
                      onClick={handleSaveAndAddAnotherCompanion}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--text-brand-default, #0066cc)",
                        textDecoration: "underline",
                        padding: 0,
                      }}
                    >
                      Save & Add Another Companion
                    </Button>
                    <Flex gap="var(--spacing-small)">
                      <Button variant="secondary" onClick={handleCancelCompanion}>
                        Cancel
                      </Button>
                      <Button variant="primary" onClick={handleSaveCompanion}>
                        {editingCompanionIndex !== null ? "Edit Companion" : "Add Companion"}
                      </Button>
                    </Flex>
                  </Flex>
                </Stack>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Select companion modal  */}
          <SelectCompanionModal
            isOpen={isSelectCompanionModalOpen}
            onClose={() => setIsSelectCompanionModalOpen(false)}
            onAddNew={() => {
              setIsSelectCompanionModalOpen(false);
              handleAddCompanion();
            }}
            onAddSelected={handleAddSelectedCompanions}
            checkboxOptions={companionCheckboxOptions}
            selectedValues={selectedExistingCompanions}
            onSelectionChange={setSelectedExistingCompanions}
            isLoading={isCompanionListLoading}
          />
        </Box>
        <Box className={styles.footer}>
          <Box className={styles.footerContent}>
            <Button variant="primary" onClick={handleAccept} loading={isAcceptingWorkOrder}>
              Accept Work Order
            </Button>
          </Box>
        </Box>
      </section>
    </Box>
  );
}
